import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Details, ValidationMessage } from '@digdir/designsystemet-react';
import type { QuestionnaireQuestion } from '../../../api/workEnvironmentTypes';
import { ArrayUtils } from '../../../utils/ArrayUtils';
import { questionElementId } from '../workEnvironmentFormView';
import classes from '../WorkEnvironmentFormSubmission.module.css';

/** One scale for every question, from never to very often */
const SCALE_LABELS = ['never', 'rarely', 'sometimes', 'often', 'veryOften'] as const;

/** The same 1–5 for an incident, in times: once counts */
const INCIDENT_LABELS = ['never', 'once', 'aFewTimes', 'monthly', 'weekly'] as const;

type Props = {
  question: QuestionnaireQuestion;
  number: number;
  total: number;
  value: number | undefined;
  onChange: (value: number) => void;
  invalid: boolean;
};

export const QuestionCard = ({ question, number, total, value, onChange, invalid }: Props): ReactElement => {
  const { t } = useTranslation();
  // Every step has words, so «2» means the same to everyone. What has happened the last
  // 12 months is counted in times, from never to every week.
  const label = (scale: number) =>
    question.isIncident
      ? t(`surveys.workEnvironmentSurveyForm.incidentLabels.${INCIDENT_LABELS[scale - 1]}`)
      : t(`surveys.workEnvironmentSurveyForm.scaleLabels.${SCALE_LABELS[scale - 1]}`);
  const id = questionElementId(question.ref);

  return (
    <fieldset id={id} className={classes.question} data-invalid={invalid || undefined}>
      <legend>{question.text}</legend>
      <div className={classes.questionText}>
        <span className={`ds-paragraph ${classes.subtle}`} data-size="xs">
          {t('workEnvironmentFormSubmission.question', { current: number, total })}
        </span>
        <p className="ds-heading" data-size="xs" aria-hidden="true">
          {question.text}
        </p>
      </div>
      <div className={classes.scale}>
        <div className={classes.options}>
          {ArrayUtils.getLickertScaleUpTo(5).map((scale) => (
            <label key={scale} className={classes.option}>
              <input
                className="ds-input"
                type="radio"
                name={id}
                value={scale}
                aria-label={`${scale} – ${label(scale)}`}
                checked={value === scale}
                onChange={() => onChange(scale)}
              />
              <span className="ds-label" aria-hidden="true">
                {scale}
              </span>
              <span className={`ds-paragraph ${classes.optionText}`} data-size="xs" aria-hidden="true">
                {label(scale)}
              </span>
            </label>
          ))}
        </div>
        {invalid && <ValidationMessage>{t('workEnvironmentFormSubmission.answerError')}</ValidationMessage>}
      </div>
      {question.help && (
        <Details>
          <Details.Summary>{t('surveys.workEnvironmentSurveyForm.explanationTitle')}</Details.Summary>
          <Details.Content>{question.help}</Details.Content>
        </Details>
      )}
    </fieldset>
  );
};
